import { Card, CardContent } from "@/components/ui/card";
import { Package, Camera, Plus, Minus } from "lucide-react";
import { PriceDisplay } from "./PriceDisplay";

export const ProductCard = ({
  product, userRole, onImageClick, onPhotoUpload, variant = "default",
  clientPrice = null, lastPrice = null, cartQty = 0, onCartChange = null,
  hidePrice = false,
}) => {
  const isTriolo = variant === "triolo";
  const isOfferte = variant === "offerte";
  // Per i prodotti Triolo: se il cliente ha uno sconto/promo memorizzato, lo mostriamo (e listino sbarrato).
  // Altrimenti: solo listino. Stessa regola per agente/admin (Triolo NON mostra mai Min/Aut).
  const trioloHasClientNet = userRole === "client" && clientPrice && clientPrice.price > 0 && clientPrice.price < (product.price_list || 0);
  const showCartControls = userRole === "client" && typeof onCartChange === "function" && !product.non_disponibile;

  return (
    <Card
      data-testid={`product-card-${product.id}`}
      className={`product-card bg-[#18181b] overflow-hidden group ${isOfferte ? 'border-red-500/10 hover:border-red-500/30' : isTriolo ? 'border-amber-500/10 hover:border-amber-500/30' : 'border-white/5 hover:border-white/10'} transition-colors ${product.non_disponibile ? 'opacity-60' : ''}`}
    >
      <div className="aspect-product relative overflow-hidden bg-[#27272a]">
        {product.image_url ? (
          <>
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
              onClick={() => onImageClick?.({ src: product.image_url, alt: product.name })}
            />
            {userRole === "admin" && onPhotoUpload && (
              <label className="absolute bottom-1.5 right-1.5 p-1.5 rounded-lg bg-black/60 hover:bg-black/80 cursor-pointer transition-colors z-10" title="Sostituisci foto">
                <Camera className="w-4 h-4 text-white/80" />
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => onPhotoUpload(product.id, e.target.files?.[0])}
                />
              </label>
            )}
          </>
        ) : userRole === "admin" && onPhotoUpload ? (
          <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-white/5 transition-colors">
            <Camera className="w-6 h-6 text-zinc-500 mb-1" />
            <span className="text-[10px] text-zinc-500">Aggiungi foto</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(e) => onPhotoUpload(product.id, e.target.files?.[0])}
            />
          </label>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-8 h-8 text-zinc-600" />
          </div>
        )}
        {product.non_disponibile && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="text-red-400 text-xs font-bold uppercase tracking-wider bg-red-500/20 px-2 py-1 rounded">Non disponibile</span>
          </div>
        )}
      </div>
      <CardContent className="p-3">
        <h3 className="font-heading text-sm font-semibold text-white mt-0.5 mb-1 line-clamp-2 leading-tight">{product.name}</h3>
        <p className="text-[10px] text-zinc-500 font-mono mb-2">{product.sku} · {product.unit}</p>
        {!hidePrice && (
        <div className="pt-2 border-t border-white/5">
          {isOfferte ? (
            <div>
              <div className="flex items-center justify-between bg-red-500/10 rounded px-1.5 py-0.5 -mx-1">
                <span className="text-[10px] text-red-400 font-semibold">PROMO</span>
                <span className="text-base font-extrabold text-red-400">
                  {product.price_promo?.toFixed(2)}
                  {userRole === "admin" && product.price_purchase > 0 && (
                    <span className="text-[9px] font-normal text-red-300 ml-1">
                      ({(((product.price_promo - product.price_purchase) / product.price_promo) * 100).toFixed(1)}%)
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between mt-0.5">
                <span className="text-[10px] text-zinc-400">Listino</span>
                <span className="text-xs text-zinc-500 line-through font-mono">{product.price_list?.toFixed(2)}</span>
              </div>
              {userRole === "admin" && product.price_purchase > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-emerald-500">Costo</span>
                  <span className="text-xs text-emerald-500 font-mono">{product.price_purchase.toFixed(2)}</span>
                </div>
              )}
            </div>
          ) : isTriolo ? (
            <div className="space-y-0.5">
              {trioloHasClientNet && (
                <div className={`flex items-center justify-between rounded px-1.5 py-0.5 -mx-1.5 ${clientPrice.source === "promo" ? "bg-red-500/15" : "bg-cyan-500/10"}`}>
                  <span className={`text-[10px] font-semibold tracking-wide uppercase ${clientPrice.source === "promo" ? "text-red-400" : "text-cyan-400"}`}>
                    {clientPrice.source === "promo" ? "PROMO" : "Tuo prezzo"}
                  </span>
                  <span className={`text-sm font-extrabold ${clientPrice.source === "promo" ? "text-red-400" : "text-cyan-400"}`}>
                    {clientPrice.price.toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-zinc-400">Listino</span>
                <span className={`font-mono text-sm font-semibold ${trioloHasClientNet ? 'text-zinc-500 line-through' : 'text-white'}`}>
                  {product.price_list.toFixed(2)}
                </span>
              </div>
              {userRole === "admin" && product.price_purchase > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-emerald-500">Costo</span>
                  <span className="font-mono text-xs text-emerald-500">{product.price_purchase.toFixed(2)}</span>
                </div>
              )}
            </div>
          ) : (
            <PriceDisplay product={product} userRole={userRole} clientPrice={clientPrice} lastPrice={lastPrice} />
          )}
        </div>
        )}
        {/* Storico ultima vendita per il cliente — sempre visibile se ha già acquistato */}
        {userRole === "client" && lastPrice && Number(lastPrice.ultima_qta) > 0 && (
          <div
            className="mt-1.5 px-1.5 py-1 rounded bg-zinc-800/40 text-[10px] text-zinc-400 flex items-center justify-between gap-1"
            data-testid={`last-purchase-${product.id}`}
          >
            <span className="font-mono">
              Ult.: <span className="text-white font-semibold">{Number(lastPrice.ultima_qta).toLocaleString("it-IT", { maximumFractionDigits: 2 })}</span>
              {product.unit ? ` ${product.unit}` : ""}
            </span>
            {lastPrice.ultima_data && (
              <span className="font-mono text-[9px] text-zinc-500">
                {(() => {
                  const d = lastPrice.ultima_data;
                  // Convert YYYY-MM-DD or ISO to DD/MM/YY
                  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
                  return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : d;
                })()}
              </span>
            )}
          </div>
        )}
        {showCartControls && (
          <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between gap-2">
            {cartQty > 0 ? (
              <div className="flex items-center gap-1.5 flex-1">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onCartChange(product, Math.max(0, cartQty - 1)); }}
                  data-testid={`cart-minus-${product.id}`}
                  className="w-8 h-8 rounded-md bg-white/5 hover:bg-white/10 text-zinc-300 flex items-center justify-center"
                  aria-label="Diminuisci"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span
                  className="flex-1 text-center text-base font-bold text-white font-mono"
                  data-testid={`cart-qty-display-${product.id}`}
                >
                  {cartQty}
                </span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onCartChange(product, cartQty + 1); }}
                  data-testid={`cart-plus-${product.id}`}
                  className="w-8 h-8 rounded-md bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 flex items-center justify-center"
                  aria-label="Aumenta"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onCartChange(product, 1); }}
                data-testid={`cart-add-${product.id}`}
                className="w-full h-8 rounded-md bg-amber-500 hover:bg-amber-600 text-black text-xs font-semibold flex items-center justify-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" />
                Aggiungi
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
